import React, { useEffect, useState } from 'react';
import { Table, DOMHelper, Stack, Divider, Checkbox, IconButton, Whisper, Tooltip, Modal, Button } from 'rsuite';
import TrashIcon from '@rsuite/icons/Trash';
import type { SortType } from 'rsuite';
import { BaseErrorModel, BaseResponseModel, PagedResponseModel, PaginationFilterRequest, PaginationFilterResult } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import ImageCell from '@/components/ImageCell';
import PageContent from '@/components/PageContent';
import { AdvanceShare, AdvanceSharePaginationFilterRequest } from '@/models/advance-share.model';
import { deleteAdvanceShare, getPagedAdvanceShares } from '@/services/advance-share.service';
import FetchingSelect from '@/components/FetchingSelect';
import { getUsersLookup } from '@/services/user.service';
import { getActivityInfosLookup } from '@/services/activity-info.service';
import { getAdvanceShareStylesLookup } from '@/services/advance-share-style.service';
import { getAdvanceShareStyleVersionsLookup } from '@/services/advance-share-style-version.service';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

interface AdvanceShareListProps {
  styleId?: number;
  styleVersionId?: number;
  activityInfoId?: number;
  userId?: number;
}
const AdvanceShareList : React.FC<AdvanceShareListProps> = ({styleId, styleVersionId, activityInfoId, userId}) => {
  const [filterReq, setFilterReq] = useState<AdvanceSharePaginationFilterRequest>(new PaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<AdvanceShare[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<AdvanceShare | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: AdvanceSharePaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {...filterReq,
        userId : userId ?? filterReq.userId,
        activityInfoId : activityInfoId ?? filterReq.activityInfoId,
        styleId : styleId ?? filterReq.styleId,
        styleVersionId : styleVersionId ?? filterReq.styleVersionId,
      };

      const result: PagedResponseModel<AdvanceShare> = await getPagedAdvanceShares(finalReq);
      
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching advance share styles:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result: BaseResponseModel<null> = await deleteAdvanceShare(deleteTarget.id);
      if (result.success) {
        toastSuccessMessage('Advance share request deleted.');
        setDeleteTarget(null);
        fetchData(filterReq);
      }
    } catch (err) {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(errorModel ?? 'Error in deleting the advance share request.');
    } finally {
      setDeleting(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}> 
          {!userId && <FetchingSelect
            label="User"
            fetchData={(text, page) => getUsersLookup(text, undefined, page)}
            value={filterReq.userId}
            onChange={dataKey => setFilterReq(pre => ({...pre, userId: dataKey}))}
          />}
          {!activityInfoId && <FetchingSelect
            label="Activity"
            fetchData={(text, page) => getActivityInfosLookup(text, page)}
            value={filterReq.activityInfoId}
            onChange={dataKey => setFilterReq(pre => ({...pre, activityInfoId: dataKey}))}
          />}
          {!styleId && <FetchingSelect
            label="Style"
            fetchData={(text, page) => getAdvanceShareStylesLookup(text, page)}
            value={filterReq.styleId}
            onChange={dataKey => setFilterReq(pre => ({...pre, styleId: dataKey}))}
          />}
          {!styleVersionId && <FetchingSelect
            label="Style Version"
            fetchData={(text, page) => getAdvanceShareStyleVersionsLookup(text, styleId, page)}
            value={filterReq.styleVersionId}
            onChange={dataKey => setFilterReq(pre => ({...pre, styleVersionId: dataKey}))}
          />}
          <Checkbox onChange={(value, checked) => setFilterReq(pre => ({...pre, isGenerated: checked == true ? true : undefined}))} >Only Generated</Checkbox>
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
        rowHeight={80}
      >
        <Column flexGrow={1} align="center">
          <HeaderCell>User Image</HeaderCell>
          <ImageCell dataKey="userImageUrl" />
        </Column>              
        <Column flexGrow={1} align="center" fullText sortable>
          <HeaderCell>Generated Image</HeaderCell>
          <ImageCell dataKey="generatedImageUrl" />
        </Column>
        <Column flexGrow={1} align="center" >
          <HeaderCell>User</HeaderCell>
          <Cell dataKey="user.name" verticalAlign='middle'/>
        </Column>
        <Column flexGrow={1} align="center" >
          <HeaderCell>Style</HeaderCell>
          <Cell dataKey="style.name" verticalAlign='middle'/>
        </Column>
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Style Version</HeaderCell>
          <Cell dataKey="style.version" verticalAlign='middle'/>
        </Column>
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Is Generated</HeaderCell>
          <Cell verticalAlign='middle'>
          {(rowData: AdvanceShare) => (   
            <Checkbox checked={rowData.isGenerated} readOnly></Checkbox>
          )}
          </Cell>
        </Column>        
        <Column flexGrow={1} align="center" >
          <HeaderCell>Activity Id</HeaderCell>
          <Cell dataKey="activityInfoId" verticalAlign='middle'/>
        </Column>
        <Column width={80} align="center" fixed="right">
          <HeaderCell>Action</HeaderCell>
          <Cell verticalAlign='middle' style={{ padding: '6px' }}>
          {(rowData: AdvanceShare) => (
            <Whisper speaker={<Tooltip>Delete</Tooltip>}>
              <IconButton
                icon={<TrashIcon />}
                color="red"
                appearance="subtle"
                size="sm"
                onClick={() => setDeleteTarget(rowData)}
              />
            </Whisper>
          )}
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />

      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} size="xs">
        <Modal.Header><Modal.Title>Delete this advance share request?</Modal.Title></Modal.Header>
        <Modal.Body>
          Request #{deleteTarget?.id} will be deleted and its generated image removed from storage.
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setDeleteTarget(null)} appearance="subtle">Back</Button>
          <Button onClick={handleDelete} appearance="primary" color="red" loading={deleting}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </PageContent>
  );
};

export default AdvanceShareList;
